import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTelegramPlane } from 'react-icons/fa';
import LoginForm from './Login/LoginForm';
import OtpForm from './Login/OTPForm';
import PasswordForm from './Login/PasswordForm';
import RegisterForm from "./Login/RegisterForm";
import { getTelegramBotLink } from '../constants/whitelabelEnv';
import { domain } from '../constants/Branding';
import { setBrowser, setIsLoggedIn } from '../store/authReducer';
import TOKEN_API from '../services/token';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

interface LoginPageProps {
  referalCode: any;
  theme?: 'default' | 'lovable';
  onRegisterClick?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ referalCode, theme = 'default', onRegisterClick }) => {
  // Current step in the authentication flow
  const [step, setStep] = useState(1);
  const [demoLoading, setDemoLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const siteConfig = useSelector((state: RootState) => state.userDetails.siteConfig);
  // User data that will be collected throughout the process
  const [userData, setUserData] = useState({
    mobileNumber: '',
    countryCode: '91',
    password: '',
    confirmPassword: '',
    username: '',
    otpToken: '',
    userId: '',
    otpMessage: '',
  });

  // Whether the user exists in the system (determined after OTP verification)
  const [userExists, setUserExists] = useState<boolean | null>(null);

  // Remaining time for verification steps
  const [otpTimeRemaining, setOtpTimeRemaining] = useState(0);
  const [passwordTimeRemaining, setPasswordTimeRemaining] = useState(0);

  // Update user data through the flow
  const updateUserData = (data: Partial<typeof userData>) => {
    setUserData(prev => ({ ...prev, ...data }));
  };

  // Advance to next step in the flow
  const goToNextStep = () => {
    setStep(prevStep => prevStep + 1);
  };

  // Go back to previous step
  const goToPreviousStep = () => {
    setStep(prevStep => Math.max(1, prevStep - 1));
  };

  // Set the verification times
  const startOtpTimer = () => {
    setOtpTimeRemaining(90); // 90 seconds for OTP verification
  };

  const startPasswordTimer = () => {
    setPasswordTimeRemaining(300); // 5 minutes (300 seconds) for password entry
  };

  // Handle OTP verification result
  const handleOtpVerified = (exists: boolean, token: string) => {
    setUserExists(exists);
    updateUserData({ otpToken: token });
    startPasswordTimer();
    goToNextStep();
  };

  // Go directly to login step
  const goToLoginStep = () => {
    setStep(1);
  };

  const telegramLink = getTelegramBotLink(domain);
  const telegramLinkWithReferral = referalCode ? `${telegramLink}?start=${referalCode}` : telegramLink;



  // Theme-based classes
  const containerBg = theme === 'lovable' 
    ? 'bg-[hsl(275_35%_16%)] border border-[hsl(275,90%,64%)]' 
    : 'bg-zinc-900';
  const containerRounded = theme === 'lovable' ? 'rounded-2xl' : 'rounded-lg';
  const innerBg = theme === 'lovable' 
    ? 'bg-[hsl(275,35%,16%)] border-[hsl(275,90%,64%)]' 
    : 'bg-zinc-800 border-zinc-700';
  const telegramBg = theme === 'lovable'
    ? 'bg-[hsl(275,35%,20%)] hover:bg-[hsl(275,35%,25%)]'
    : 'bg-zinc-700 hover:bg-zinc-600 border-zinc-600';
  const showBorder = theme === 'lovable' ? '' : 'border';
  const descriptionTextClass = theme === 'lovable' ? 'text-[hsl(275_50%_65%)]' : 'text-zinc-400';

  return (
    <div className={`w-full max-w-md mx-auto p-6 pt-8 ${containerBg} text-white ${containerRounded} shadow-lg`}>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white">
            {step === 1 && 'Sign In'}
            {step === 2 && 'Verify OTP'}
            {step === 3 && 'Complete Verification'}
          </h2>
        </div>
        <p className={`text-sm ${descriptionTextClass}`}>
          {step === 1 && "Please enter your Mobile Number"}
          {step === 2 && "Enter the OTP sent to your Phone"}
          {step === 3 && "Complete your Account Information"}
        </p>
      </div>

      <div className={`rounded-lg ${innerBg} ${showBorder} mb-4`}>
        <a
          href={telegramLinkWithReferral}
          target="_blank"
          rel="noopener noreferrer"
          className="no-underline"
        >
          <div className={`flex items-center gap-1.5 sm:gap-2 p-2 sm:p-3 ${telegramBg} rounded-md cursor-pointer transition-colors ${showBorder}`}>
            <FaTelegramPlane className="text-blue-400 w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
            <span className="text-xs sm:text-sm text-blue-400">
              Try out our site and Play on Telegram
            </span>
          </div>
        </a>
      </div>

      <div className={`p-4 rounded-lg ${innerBg} border`}>
        {step === 1 && (
          <LoginForm
            userData={userData}
            updateUserData={updateUserData}
            goToNextStep={goToNextStep}
            startOtpTimer={startOtpTimer}
            theme={theme}
            onRegisterClick={onRegisterClick}
          />
        )}

        {step === 2 && (
          <OtpForm
            userData={userData}
            timeRemaining={otpTimeRemaining}
            setTimeRemaining={setOtpTimeRemaining}
            onOtpVerified={handleOtpVerified}
            goToPreviousStep={goToPreviousStep}
            updateUserData={updateUserData}
          />
        )}

        {step === 3 && userExists === true && (
          <PasswordForm
            userData={userData}
            updateUserData={updateUserData}
            timeRemaining={passwordTimeRemaining}
            setTimeRemaining={setPasswordTimeRemaining}
            goToPreviousStep={goToPreviousStep}
            goToLoginStep={goToLoginStep}
          />
        )}

        {step === 3 && userExists === false && (
          <RegisterForm
            userData={userData}
            updateUserData={updateUserData}
            timeRemaining={passwordTimeRemaining}
            setTimeRemaining={setPasswordTimeRemaining}
            goToPreviousStep={goToPreviousStep}
            referal={referalCode}
            goToLoginStep={goToLoginStep}
          />
        )}
      </div>
    </div>
  );
};

export default LoginPage;